import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { Box, Typography, Stack, Divider, Link } from '@mui/material'
import {
  CommonButton,
  CommonSelect,
  CommonDatePicker,
} from '@/components/common'
import {
  ResultCard,
  DonorPortalScreenMessage,
  SuggestionsSection,
  InfiniteScrollSentinel,
} from '@/components/donation'
import {
  useGetProcedureTypes,
  useGetSponsors,
  useGetSponsorSearch,
  useInfiniteScrollResults,
} from '@/hooks'
import { useDonorContext } from '@/context'
import { ROUTES, DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import { ResultCardSkeleton } from '@/pages/donate-near-you/results/Skeletons'
import {
  buildResultItem,
  formatIsoDateWithWeekday,
  getDonorEligibilityForScheduleRow,
  getScheduledDonationsParams,
  getSearchBySponsorParams,
  isScheduleRowBookable,
  isValidSponsorAccountId,
  isDateRangeSearchValid,
  isOpenEndedDateRangeSearch,
  parseSearchBySponsorParams,
} from '@/utils'

function isoYmdToLocalDate(iso) {
  if (!iso || typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso))
    return null
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function canFetchSponsorSearch(fd) {
  if (!isValidSponsorAccountId(fd?.account_id)) return false
  if (!isDateRangeSearchValid(fd.start_date, fd.end_date)) return false
  return true
}

export const SearchBySponsorResults = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { procedureTypes, loadingProcedureTypes, getProcedureTypes } =
    useGetProcedureTypes()
  const { sponsors, loadingSponsors, getSponsors } = useGetSponsors()
  const {
    sponsorSearchResults,
    sponsorSearchSuggestions,
    getSponsorSearch,
    loadingSponsorSearch,
    clearSponsorSearch,
  } = useGetSponsorSearch()
  const { donor, getDonor } = useDonorContext()

  const [formData, setFormData] = useState({})

  useEffect(() => {
    getProcedureTypes()
    getSponsors()
  }, [])

  useEffect(() => {
    getDonor()
  }, [getDonor])

  const searchQuery = searchParams.toString()

  useEffect(() => {
    const parsed = parseSearchBySponsorParams(searchParams)
    const fd = {
      ...parsed,
      start_date: isoYmdToLocalDate(parsed.start_date),
      end_date: isoYmdToLocalDate(parsed.end_date),
    }
    setFormData(fd)
    if (canFetchSponsorSearch(fd)) getSponsorSearch(fd)
    else clearSponsorSearch()
  }, [searchQuery, searchParams, getSponsorSearch, clearSponsorSearch])

  const sponsorOptions = useMemo(
    () =>
      (sponsors || []).map(s => ({
        id: String(s.id),
        name: s.name,
      })),
    [sponsors]
  )

  const handleFormDataChange = useCallback(
    (key, value) => {
      setFormData(prev => {
        const updated = { ...prev, [key]: value }
        if (!canFetchSponsorSearch(updated)) clearSponsorSearch()
        return updated
      })
    },
    [clearSponsorSearch]
  )

  const runSearchFromFilters = useCallback(() => {
    if (!canFetchSponsorSearch(formData)) return
    getSponsorSearch(formData)
    const params = getSearchBySponsorParams(formData)
    navigate(`${ROUTES.DONATE_SPONSOR_RESULTS}?${params.toString()}`)
  }, [formData, navigate, getSponsorSearch])

  const filterSearchDisabled = useMemo(
    () => !canFetchSponsorSearch(formData),
    [formData]
  )

  const builtResults = useMemo(() => {
    if (!sponsorSearchResults?.length) return []
    return sponsorSearchResults.map(row => buildResultItem(row, {}))
  }, [sponsorSearchResults])

  const useInfiniteScroll = isOpenEndedDateRangeSearch(
    formData.start_date,
    formData.end_date
  )

  const {
    visibleItems: visibleBuiltResults,
    hasMore: hasMoreResults,
    loadingMore: loadingMoreResults,
    sentinelRef: resultsSentinelRef,
  } = useInfiniteScrollResults(builtResults, 20, useInfiniteScroll)

  const visibleRawResults = useMemo(() => {
    if (!sponsorSearchResults?.length) return []
    return sponsorSearchResults.slice(0, visibleBuiltResults.length)
  }, [sponsorSearchResults, visibleBuiltResults.length])

  const builtSuggestions = useMemo(() => {
    if (!sponsorSearchSuggestions?.length) return []
    return sponsorSearchSuggestions.map(row => buildResultItem(row, {}))
  }, [sponsorSearchSuggestions])

  const selectedSponsorName = useMemo(() => {
    const match = sponsorOptions.find(
      option => String(option.id) === String(formData.account_id)
    )
    return match?.name
  }, [formData.account_id, sponsorOptions])

  const handleSelectResult = useCallback(
    (resultItem, rawRow) => {
      if (!isScheduleRowBookable(rawRow)) return
      const { applies, selectable } = getDonorEligibilityForScheduleRow({
        donorId: donor?.id,
        procedureEligibilities: donor?.procedure_eligibilities,
        selectedProcedureTypeId: formData.procedure_type_id,
        appointmentDayIso: resultItem.date,
        procedureTypesOnRow: rawRow?.procedure_types,
      })
      if (applies && !selectable) return
      navigate(
        `${ROUTES.SCHEDULE_DONATION}?${getScheduledDonationsParams({
          ...resultItem,
          ...formData,
          date: resultItem.date,
        }).toString()}`
      )
    },
    [donor?.id, donor?.procedure_eligibilities, formData, navigate]
  )

  const renderScheduleResultCard = useCallback(
    (resultItem, rawRow, index) => {
      const elig = getDonorEligibilityForScheduleRow({
        donorId: donor?.id,
        procedureEligibilities: donor?.procedure_eligibilities,
        selectedProcedureTypeId: formData.procedure_type_id,
        appointmentDayIso: resultItem.date,
        procedureTypesOnRow: rawRow?.procedure_types,
      })
      const scheduleBlocked =
        !isScheduleRowBookable(rawRow) || (elig.applies && !elig.selectable)

      return (
        <ResultCard
          key={`${resultItem.operation_id}-${resultItem.date}-${index}`}
          item={resultItem}
          operationDate={
            resultItem.date
              ? formatIsoDateWithWeekday(resultItem.date)
              : undefined
          }
          onSelect={() => handleSelectResult(resultItem, rawRow)}
          isSelectDisabled={scheduleBlocked}
          selectionSurface='buttonOnly'
          eligibilityChip={elig.applies ? elig.chip : null}
        />
      )
    },
    [
      donor?.id,
      donor?.procedure_eligibilities,
      formData.procedure_type_id,
      handleSelectResult,
    ]
  )

  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <DonorPortalScreenMessage
        code={DONOR_PORTAL_SCREEN_CONFIG_CODE.SEARCH_BY_SPONSOR_RESULTS}
      />
      <Typography variant='h5' component='h1' fontWeight={600}>
        Search by Sponsor
      </Typography>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ alignItems: { md: 'flex-start' }, flexWrap: 'wrap' }}
        >
          <Box sx={{ flex: { md: '2 1 200px' }, minWidth: 180 }}>
            <CommonSelect
              id='sbs-res-sponsor'
              label='Sponsor'
              placeholder='Select a sponsor'
              enableSort={false}
              options={sponsorOptions}
              value={formData.account_id ?? ''}
              loading={loadingSponsors}
              onChange={v => handleFormDataChange('account_id', v || '')}
              required
            />
          </Box>
          <Box sx={{ flex: { md: '1 1 140px' }, minWidth: 140 }}>
            <CommonDatePicker
              id='sbs-res-start'
              label='Start date'
              value={formData.start_date ?? null}
              onChange={d => handleFormDataChange('start_date', d)}
              disablePast
              required
            />
          </Box>
          <Box sx={{ flex: { md: '1 1 140px' }, minWidth: 140 }}>
            <CommonDatePicker
              id='sbs-res-end'
              label='End date'
              value={formData.end_date ?? null}
              onChange={d => handleFormDataChange('end_date', d)}
              disablePast
              helperText='Leave blank to show all future dates'
            />
          </Box>
          <Box sx={{ flex: { md: '2 1 200px' }, minWidth: 180 }}>
            <CommonSelect
              id='sbs-res-procedure'
              label='Donation type'
              placeholder='All Available'
              options={procedureTypes}
              loading={loadingProcedureTypes}
              value={formData.procedure_type_id ?? ''}
              onChange={v => handleFormDataChange('procedure_type_id', v || '')}
            />
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CommonButton
            id='sbs-res-search'
            label='Search'
            variant='primary'
            disabled={filterSearchDisabled}
            onClick={runSearchFromFilters}
          />
        </Box>
      </Stack>

      <Divider />

      <Box sx={{ mt: 2 }}>
        <SuggestionsSection
          loading={loadingSponsorSearch}
          hasResults={visibleBuiltResults.length > 0}
          hasSuggestions={builtSuggestions.length > 0}
          suggestionsSubtitle={
            selectedSponsorName
              ? `Here are upcoming sessions for ${selectedSponsorName} on other dates`
              : undefined
          }
        >
          <Stack spacing={2}>
            {builtSuggestions.map((resultItem, index) =>
              renderScheduleResultCard(
                resultItem,
                sponsorSearchSuggestions[index],
                index
              )
            )}
          </Stack>
        </SuggestionsSection>
        <Stack spacing={2}>
          {loadingSponsorSearch
            ? Array.from({ length: 5 }).map((_, index) => (
                <ResultCardSkeleton key={`skeleton-${index}`} />
              ))
            : visibleBuiltResults.map((resultItem, index) =>
                renderScheduleResultCard(
                  resultItem,
                  visibleRawResults[index],
                  index
                )
              )}
          <InfiniteScrollSentinel
            sentinelRef={resultsSentinelRef}
            loadingMore={loadingMoreResults}
            hasMore={useInfiniteScroll && hasMoreResults}
          />
        </Stack>
      </Box>

      <Box sx={{ pt: 2, textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to={`${ROUTES.DONATE_SPONSOR}?mode=private_event`}
          variant='body2'
          underline='hover'
        >
          Have a private event number?
        </Link>
      </Box>
    </Stack>
  )
}

export default SearchBySponsorResults
